/**
 * useKillTeamReconcile - the GM's screen puts Squad Mode right where nobody pressed anything (Squad Mode stage 6):
 * a kill-team with an Oath and a leader here and no pool yet gets one, filled at the start its sheets work out, and a
 * pool filled for another Oath is reset to the new one (plan modes-rework - nothing is sworn; the sheets say);
 * a brother in Squad Mode who is down or out of the fight drops to Solo, so does one who is no longer a brother (moved
 * to another team, or his kill-team plays no Oath any more), and a pool that READS 0 while a brother is
 * still in Squad Mode drops them all (a step that emptied it on another screen does this itself; this catches what
 * was missed, like a write lost to a closed tab). Never from a missing pool (gotcha 13).
 *
 * The GM's screen alone, so two screens never both write it. Keyed on what is due, so it runs once per change.
 */
import { useEffect, useRef } from 'react';
import { oathPlan, reconcileDue } from '../engine/killTeam/acts';
import { teamOf } from '../engine/roster/team';
import { isBrother, type KillTeams } from '../engine/killTeam/record';
import type { Combatant } from '../engine/roster/types';
import type { CohesionDoc } from '../engine/killTeam/pool';
import type { KillTeamActions } from '../contexts/KillTeamsContext';
import type { KillTeamsState } from './useKillTeams';

interface Args {
  isGm: boolean;
  roster: readonly Combatant[];
  /** The kill-teams and their pools (useKillTeams); null with none. */
  killTeams: Pick<KillTeamsState, 'teams' | 'cohesion'> | null;
  actions: KillTeamActions;
}

/** What is due, as a key that changes only when it does; '' for nothing (or not the GM's screen). */
function dueKey({ isGm, roster, killTeams }: Omit<Args, 'actions'>): string {
  const cohesion = killTeams?.cohesion;
  if (!isGm || !cohesion) return '';
  const { teams } = killTeams;
  const due = {
    ...reconcileDue(
      roster,
      cohesion.pools,
      (c) => teamOf(c)?.tag,
      (c, tag) => playsOath(teams, tag) && isBrother(c, tag),
    ),
    fills: fillsDue(teams, cohesion),
  };
  return due.down.length || due.strays.length || due.emptied.length || due.fills.length ? JSON.stringify(due) : '';
}

/**
 * Whether the team still plays an Oath: one whose block was taken off the sheets, or its Oath cleared, leaves its
 * brothers in Squad Mode with no strip to leave by - they are strays (the stage review).
 */
const playsOath = (teams: KillTeams, tag: string): boolean => !!teams.killTeams[tag]?.oath;

/** The kill-teams whose pool is not yet the one their sheets want (`oathPlan` says when: an Oath and a leader). */
const fillsDue = (teams: KillTeams, cohesion: CohesionDoc): string[] =>
  Object.values(teams.killTeams).flatMap((kt) => (oathPlan(kt, cohesion.pools[kt.tag] ?? null, []) ? [kt.tag] : []));

type Due = { down: string[]; strays: string[]; emptied: string[]; fills: string[] };

/**
 * Each thing due, through the verbs (each its own write; none waits for another). A fill still on its way is not
 * sent again when something else changes the key: its compare-and-set would only refuse the second, aloud.
 */
function carryDue({ down, strays, emptied, fills }: Due, actions: KillTeamActions, filling: Set<string>): void {
  for (const tag of fills.filter((t) => !filling.has(t))) {
    filling.add(tag);
    void actions.fill(tag).finally(() => filling.delete(tag));
  }
  for (const id of down) void actions.drop(id, 'is down: back to Solo Mode');
  for (const id of strays) void actions.stray(id);
  for (const tag of emptied) void actions.empty(tag);
}

export default function useKillTeamReconcile({ actions, ...facts }: Args): void {
  const key = dueKey(facts);
  const filling = useRef(new Set<string>());
  useEffect(() => {
    if (key) carryDue(JSON.parse(key) as Due, actions, filling.current);
  }, [key, actions]);
}
